import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Badge, Card, Empty, ErrorBox, Field, KV, Modal, Page, Spinner, StatusBadge,
  SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { FLAG, fmtDate, fmtStamp, inr, lakh } from '../../lib/format.js'

export default function FlagDecision () {
  const { id } = useParams()
  const toast = useToast()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsync(() => api.get(`/operator/batches/${id}/flag`), [id])
  const [modal, setModal] = useState(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const decide = async which => {
    const releasing = which === 'override'
    const ok = await confirm({
      title: releasing ? 'Clear this batch to keep selling?' : 'Hold every booking on this batch?',
      body: releasing
        ? 'You are overriding a quality flag raised by failed sample tests. The batch can be matched and booked again, and your name stays on the override.'
        : 'Bookings on this batch stop moving until someone decides otherwise. The farmers on them will need calling.',
      facts: [
        ['Batch', data.batch.batchCode],
        ['Hatchery', data.batch.hatchery?.name],
        ['Failed samples', `${data.batch.sampleFailCount}`],
        ['Note', note || 'none recorded']
      ],
      confirmLabel: releasing ? 'Override the flag' : 'Hold the bookings',
      tone: 'danger'
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.post(`/operator/batches/${id}/${which}`, { note })
      toast.ok(res.message)
      setModal(null)
      setNote('')
      reload()
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  if (loading) return <Page title="Batch decision"><Spinner /></Page>
  if (error) return <Page title="Batch decision"><ErrorBox error={error} onRetry={reload} /></Page>

  const { batch: b, atRisk, failures, settings } = data
  const red = b.qualityFlag === 'red_flag'

  return (
    <Page
      crumb={<Link to="/operator">← Operator desk</Link>}
      title={`${b.batchCode} — quality decision`}
      subtitle={`${b.hatchery.name} · ${b.plStage} ready ${fmtDate(b.readyDate)} · ${b.samplePassCount} passed / ${b.sampleFailCount} failed`}
    >
      <div className={`alert ${red ? 'error' : 'warn'}`}>
        <div>
          <b>{FLAG[b.qualityFlag].label}</b>
          {b.flagNote}
          {b.override?.at && <div style={{ marginTop: 6 }}>Overridden {fmtStamp(b.override.at)}{b.override.note ? ` — ${b.override.note}` : ''}</div>}
        </div>
      </div>

      <div className="alert info">
        <div>
          <b>Read this before deciding.</b>
          A sample can fail for two very different reasons: bad seed from the hatchery, or a pond that was
          not ready on the farmer's side. One failure proves nothing — that is why a single failure only
          puts the batch under observation and lets other bookings run. {red
            ? `This batch has ${b.sampleFailCount} failures from different farmers, which is a real signal. Call the hatchery, then decide below.`
            : 'Call the hatchery and check the farmer\'s pond conditions before the next dispatch.'}
        </div>
      </div>

      <div className="split">
        <div>
          <Card title={`Failed sample tests (${failures.length})`} padded={false}>
            <div className="scroll-x">
              <table>
                <thead>
                  <tr><th>Order</th><th>Farmer</th><th className="num">Survival</th><th>Stocking date</th><th>What the farmer said</th></tr>
                </thead>
                <tbody>
                  {failures.map(f => (
                    <tr key={f._id}>
                      <td><Link className="rowlink mono" to={`/operator/orders/${f._id}`}>{f.orderCode}</Link></td>
                      <td>{f.farmer?.name}<span className="t-sub">{f.farmer?.village} · {f.farmer?.phone}</span></td>
                      <td className="num">{f.sample?.survivalPct}%<span className="t-sub">{f.sample?.survivors}/{f.sample?.animalsSent}</span></td>
                      <td className="tight">{fmtDate(f.targetStockingDate)}</td>
                      <td className="small">{f.sample?.farmerRemarks || '—'}</td>
                    </tr>
                  ))}
                  {!failures.length && <tr><td colSpan={5}><Empty>No failures recorded on this batch.</Empty></td></tr>}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title={`Bookings still riding on this batch (${atRisk.length})`} padded={false}>
            <div className="scroll-x">
              <table>
                <thead>
                  <tr><th>Order</th><th>Farmer</th><th>Stocking</th><th className="num">Qty</th><th className="num">Value</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {atRisk.map(o => (
                    <tr key={o._id}>
                      <td><Link className="rowlink mono" to={`/operator/orders/${o._id}`}>{o.orderCode}</Link></td>
                      <td>{o.farmer?.name}<span className="t-sub">{o.farmer?.phone}</span></td>
                      <td className="tight">{fmtDate(o.targetStockingDate)}</td>
                      <td className="num">{lakh(o.quantityLakh)}</td>
                      <td className="num">{inr(o.totalAmount)}</td>
                      <td><StatusBadge status={o.status} /></td>
                    </tr>
                  ))}
                  {!atRisk.length && <tr><td colSpan={6}><Empty>No other bookings are exposed to this batch.</Empty></td></tr>}
                </tbody>
              </table>
            </div>
            {atRisk.length > 0 && (
              <div className="body tight muted small">
                These farmers have not been told anything. Whatever you decide, the platform contacts them — the
                hatchery is informed either way.
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card title="Your decision">
            <p className="muted small" style={{ marginTop: 0 }}>
              {red
                ? 'Nothing further ships from this batch until you choose.'
                : 'The batch is under observation. You can still pull it if the hatchery cannot explain the failure.'}
            </p>
            <div className="btnrow" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <button className="btn primary block" onClick={() => setModal('override')}>
                Proceed — the seed is fine
              </button>
              <span className="muted small">Clears the flag. Pending bookings on this batch can be dispatched.</span>
              <div className="divider" />
              <button className="btn danger block" onClick={() => setModal('hold')}>
                Pull the batch from dispatch
              </button>
              <span className="muted small">Closes the batch and marks {atRisk.length} booking(s) for re-matching elsewhere.</span>
            </div>
          </Card>

          <Card title="Batch record">
            <KV items={[
              { k: 'Hatchery', v: <Link to={`/admin/hatcheries/${b.hatchery._id}`}>{b.hatchery.name}</Link> },
              { k: 'Broodstock', v: b.broodstockSource },
              { k: 'Stress test', v: `${b.stressTestPct ?? '—'}%` },
              { k: 'Size CV', v: `${b.sizeCvPct ?? '—'}%` },
              { k: 'Salinity', v: `${b.salinityPpt ?? '—'} ppt` },
              { k: 'Available', v: lakh(b.availableLakh) },
              { k: 'Pass threshold', v: `${settings.pass_threshold_pct}%` },
              { k: 'PCR', v: b.pcr?.reportPath ? <a href={b.pcr.reportPath} target="_blank" rel="noreferrer">Open certificate</a> : <Badge tone="warn">Missing</Badge> }
            ]} />
          </Card>
        </div>
      </div>

      {modal && (
        <Modal
          title={modal === 'override' ? 'Clear the flag and proceed' : 'Pull this batch from dispatch'}
          onClose={() => setModal(null)}
        >
          <form onSubmit={e => { e.preventDefault(); decide(modal) }}>
            <p className="muted small" style={{ marginTop: 0 }}>
              {modal === 'override'
                ? 'Recorded against every pending booking on this batch, so the reasoning is on file if it fails again.'
                : `The batch closes and ${atRisk.length} booking(s) will need an alternative lot for the same stocking dates.`}
            </p>
            <Field label="What did the hatchery say?" required>
              <textarea value={note} onChange={e => setNote(e.target.value)} required autoFocus
                placeholder={modal === 'override'
                  ? 'e.g. Both farmers stocked into ponds with a pH crash; hatchery re-tested the tank at 96% survival.'
                  : 'e.g. Hatchery confirms an EHP scare in that tank — pulling the whole batch.'} />
            </Field>
            <div className="btnrow">
              <SubmitButton busy={busy} type="submit" className={`btn ${modal === 'override' ? 'primary' : 'danger'}`}>
                {modal === 'override' ? 'Clear flag & proceed' : 'Pull the batch'}
              </SubmitButton>
              <button type="button" className="btn" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </Page>
  )
}
